// JSON-LD for PnL and prize-claim variants of the market page (same
// underlying URL, different query params). All three are user-specific
// snapshots posted as social proof — modelled as `SocialMediaPosting`.

import type { ShareSurface } from '@/lib/share/events';

import copy from '../copy';
import { organizationId, websiteId } from './site';
import type { JsonLdBuilderContext, JsonLdPayload } from './types';

function buildSnapshotJsonLd(ctx: JsonLdBuilderContext, fallbackHeadline: string): JsonLdPayload {
  const headline = ctx.name ?? fallbackHeadline;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${ctx.pageUrl}#webpage`,
      url: ctx.pageUrl,
      name: headline,
      ...(ctx.description !== undefined ? { description: ctx.description } : {}),
      primaryImageOfPage: { '@type': 'ImageObject', url: ctx.ogImageUrl },
      isPartOf: { '@id': websiteId() },
      publisher: { '@id': organizationId() },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'SocialMediaPosting',
      '@id': `${ctx.pageUrl}#post`,
      headline,
      ...(ctx.description !== undefined ? { articleBody: ctx.description } : {}),
      url: ctx.pageUrl,
      image: ctx.ogImageUrl,
      publisher: { '@id': organizationId() },
    },
  ];
}

export function buildPnlMarketJsonLd(
  _surface: Extract<ShareSurface, { kind: 'pnl_market' }>,
  ctx: JsonLdBuilderContext,
): JsonLdPayload {
  return buildSnapshotJsonLd(ctx, copy.marketDetail.shareTitlePosition);
}

export function buildPnlPositionJsonLd(
  _surface: Extract<ShareSurface, { kind: 'pnl_position' }>,
  ctx: JsonLdBuilderContext,
): JsonLdPayload {
  return buildSnapshotJsonLd(ctx, copy.marketDetail.shareTitlePosition);
}

export function buildPrizeClaimJsonLd(
  _surface: Extract<ShareSurface, { kind: 'prize_claim' }>,
  ctx: JsonLdBuilderContext,
): JsonLdPayload {
  return buildSnapshotJsonLd(ctx, copy.marketDetail.shareTitleClaim);
}
